import { Grid, Link, Typography } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { FormattedMessage, useIntl } from 'react-intl'
import Sequence from '../Sequence'
import { useUserSession } from '@utils/useUserSession'
import {
  useProductFromQueryParam,
  getProductSequenceInfoText,
  InsertModificationArgs,
  biTubeSequenceTitle,
  SequenceType,
  biTubeSequenceLabel,
  useProductInput,
  shouldRenderOverHangDropdown,
  shouldRenderAutoGenerate,
  shouldConvertTBasesToUBases,
} from '../../ProductsConfiguration'
import OligoName from '../OligoName'
import Modifications from '../Modifications/Modifications'
import PageError from '../PageError'
import useFormikHelper from '../../useFormikHelper'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import { useEffect, useState } from 'react'
import OligoProperties from '../OligoProperties'
import CsrOverride from '../CsrOverride'
import SpecificationsHome from '../Specifications/SpecificationsHome'
import HoverToolTip from '@src/components/HoverToolTip'
import BiTubeSequencePreview from '../BiTubeSequencePreview'
import { BiTubeProduct } from '../../../BiTube/enum'
import { checkModErrorsAndScroll } from '../../utils'
import OverHangModifications from '../Modifications/OverHangModifications'
import CircleLoader from '@src/components/CircleLoader'
import AutoGenerate from '../AutoGenerate'
import useTubeValidation from '../../useTubeValidation'
import {
  GetAntisenceSeqQuery,
  GetAntisenceSeqDocument,
} from '@src/queries/ConfiguratorTubesQuery.generated'
import { useApolloClient } from '@apollo/client'

interface BiTubesProps {
  setLastCursorPosition: React.Dispatch<React.SetStateAction<number>>
  insertModificationIntoSequence: ({
    value,
    resetMod,
    isAntiSense,
  }: InsertModificationArgs) => Promise<string>
}
const useStyles = makeStyles((theme: Theme) => ({
  displayInlineFlex: {
    display: 'flex',
  },
  showBasesLink: {
    cursor: 'pointer',
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.primary.main,
    display: 'block',
  },
  sequenceEntryTitleStyle: {
    borderBottom: `1px solid ${theme.palette.grey[500]}`,
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  sequencePreviewContainer: {
    marginTop: theme.spacing(4),
    borderBottom: `1px solid ${theme.palette.grey[500]}`,
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(6),
  },
  titleStyle: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 900,
    textTransform: 'uppercase',
    display: 'inline',
    marginRight: theme.spacing(4),
  },
  disabledTitle: {
    color: theme.palette.grey[500],
  },
  baseContainer: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(8),
  },
}))

const BiTubes: React.FC<BiTubesProps> = ({
  setLastCursorPosition,
  insertModificationIntoSequence,
}) => {
  const intel = useIntl()
  const classes = useStyles()
  const { userSession } = useUserSession()

  const formik = useFormikHelper()
  const product = useProductFromQueryParam()
  const { validationAPI } = useTubeValidation()
  const productDetails = useProductInput()
  const client = useApolloClient()
  const sequenceInfoText = getProductSequenceInfoText(
    product,
    userSession.country,
    formik.selectedOligoValues?.probeType
  )
  const [showBasesForDonor, setShowBasesForDonor] = useState(false)
  const toggleShowBasesForDonor = () => {
    setShowBasesForDonor(!showBasesForDonor)
  }

  const [showBasesForAcceptor, setShowBasesForAcceptor] = useState(false)
  const toggleShowBasesForAcceptor = () => {
    setShowBasesForAcceptor(!showBasesForAcceptor)
  }
  const showOverhangDropdown = shouldRenderOverHangDropdown(product)
  const showAdditionalLabel = product === BiTubeProduct.LightCycler
  const enableAddBase = product === BiTubeProduct.LightCycler
  const enableAddModification =
    product === BiTubeProduct.Duplex || product === BiTubeProduct.mirna
  const enableInternalModification = product === BiTubeProduct.LightCycler
  const convertTBasesToUBases = shouldConvertTBasesToUBases(product)
  const showAutoGenerate = shouldRenderAutoGenerate(product)
  const isMirna = product === BiTubeProduct.mirna
  const autoGenerateName = (name: string) => {
    const suffix = product === BiTubeProduct.mirna ? '_s' : '_as'
    const nameField = product === BiTubeProduct.mirna ? 'name' : 'antisenceName'
    formik.setOligoFieldValue(nameField, `${name}${suffix}`, false)
    validationAPI({
      ...formik.validationFields,
      [nameField]: `${name}${suffix}`,
    })
  }
  const autoGenerateSequence = async (
    sequence: string,
    overhang: string,
    doNotTriggerValidate?: boolean
  ) => {
    //get new sequence using API and update the value to antisenceSequence
    const { data } = await client.query<GetAntisenceSeqQuery>({
      query: GetAntisenceSeqDocument,
      fetchPolicy: 'network-only',
      variables: {
        seq: sequence,
        product: productDetails,
      },
    })
    let antisenceSequence = ''
    if (data?.getAntisenceSeq?.antisenceSeq) {
      antisenceSequence = data?.getAntisenceSeq?.antisenceSeq
      formik.setFieldError('antisenceSequence', '')
    }
    formik.setFieldTouched('antisenceSequence', true, false)
    formik.setFieldTouched('customerAntisenseOverHang', true, false)
    formik.setOligoFieldValue('antisenceSequence', antisenceSequence, false)
    formik.setOligoFieldValue('customerAntisenseOverHang', overhang, false)
    !doNotTriggerValidate &&
      validationAPI({
        ...formik.validationFields,
        antisenceSequence: antisenceSequence,
      })
  }
  const insertModificationIntoSequenceForSense = async ({
    value,
    resetMod,
    isAntiSense,
  }: InsertModificationArgs) => {
    const updatedSequence = await insertModificationIntoSequence({
      value,
      resetMod,
      isAntiSense,
      triggerValidate: false,
    })
    formik.selectedOligoValues?.autoGenerateOligo &&
      (await autoGenerateSequence(
        updatedSequence,
        formik.selectedOligoValues?.customerOverhang || '',
        true
      ))
    return updatedSequence
  }

  useEffect(() => {
    formik.selectedOligoValues.displayPageError &&
      formik.errors &&
      checkModErrorsAndScroll({ validationObj: formik.errors, isBiTube: true })
  }, [formik.errors])

  const validSense = product === BiTubeProduct.mirna || formik.isValidSense()
  const isBitubeProduct = Object.values(BiTubeProduct).includes(
    product as BiTubeProduct
  )
  return (
    <Grid container spacing={4}>
      <Grid
        item
        xs={12}
        style={{
          marginTop: product !== BiTubeProduct.LightCycler ? '16px' : 'inherit',
        }}
      >
        <PageError />
      </Grid>
      <Grid
        item
        container
        id="donor-sequence-entry"
        style={{
          marginTop: product !== BiTubeProduct.LightCycler ? '16px' : 'inherit',
        }}
      >
        <Grid item xs={12} className={classes.sequenceEntryTitleStyle}>
          <Typography variant="h2">
            {biTubeSequenceTitle(product, SequenceType.Primary)}
          </Typography>
        </Grid>
        <Grid item xs={12} container spacing={6}>
          <Grid item xs={12}>
            <Typography variant="body1">
              *
              <FormattedMessage
                id="REQUIRED_FIELDS"
                defaultMessage="Required Fields"
              />
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <OligoName
              isAntiSense={isMirna ? true : false}
              autoGenerateName={autoGenerateName}
            />
          </Grid>
          <Grid item xs={showOverhangDropdown ? 9 : 12}>
            <Sequence
              label={intel.formatMessage(
                biTubeSequenceLabel(product, SequenceType.Primary)
              )}
              infoText={sequenceInfoText}
              setLastCursorPosition={setLastCursorPosition}
              isAntiSense={isMirna ? true : false}
              additionalLabel={
                showAdditionalLabel
                  ? intel.formatMessage({
                      id: 'THREE_MOD_FLUORESCEIN',
                      defaultMessage: "3' Modification: Fluorescein",
                    })
                  : undefined
              }
              convertTBasesToUBases={convertTBasesToUBases}
              autoGenerateSequence={autoGenerateSequence}
            />
          </Grid>
          {showOverhangDropdown && (
            <Grid item xs={3}>
              <OverHangModifications
                name="customerOverhang"
                disabled={false}
                label={
                  <FormattedMessage
                    id="CONF_OVERHANG_LABEL"
                    defaultMessage="Overhang"
                  />
                }
              />
            </Grid>
          )}
          {enableAddModification && (
            <Grid item xs={12}>
              <Modifications
                insertModificationIntoSequence={
                  insertModificationIntoSequenceForSense
                }
                showCommonAccordion
                sequenceType={
                  isMirna ? SequenceType.Secondary : SequenceType.Primary
                }
              />
            </Grid>
          )}
        </Grid>
        {enableAddBase && (
          <Grid item container xs={12} className={classes.baseContainer}>
            <Link
              onClick={toggleShowBasesForDonor}
              className={classes.showBasesLink}
            >
              <span className={classes.displayInlineFlex}>
                {showBasesForDonor ? (
                  <>
                    <ExpandMoreIcon />
                  </>
                ) : (
                  <ExpandLessIcon />
                )}
                <FormattedMessage
                  id="ADD_BASES_OPTIONAL"
                  defaultMessage="Add Bases (optional)"
                />
              </span>
            </Link>

            {showBasesForDonor && (
              <Grid item xs={12}>
                <Modifications
                  insertModificationIntoSequence={
                    insertModificationIntoSequence
                  }
                  showCommonAccordion={false}
                  sequenceType={SequenceType.Primary}
                />
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
      <Grid
        container
        item
        style={{ marginTop: '16px' }}
        id="acceptor-sequence-entry"
      >
        <Grid item xs={12} className={classes.sequenceEntryTitleStyle}>
          <Typography variant="h2">
            {biTubeSequenceTitle(product, SequenceType.Secondary)}
          </Typography>
        </Grid>
        <Grid item xs={12} container spacing={isMirna ? 2 : 6}>
          {showAutoGenerate && (
            <AutoGenerate
              autoGenerateName={autoGenerateName}
              autoGenerateSequence={autoGenerateSequence}
              disabled={!validSense}
            />
          )}
          <Grid item xs={12}>
            <OligoName
              isAntiSense={isMirna ? false : true}
              disabled={
                !!formik.selectedOligoValues.senseNameCheck || !validSense
              }
            />
          </Grid>
          <Grid item xs={showOverhangDropdown ? 9 : 12}>
            {!isMirna && (
              <Sequence
                label={intel.formatMessage(
                  biTubeSequenceLabel(product, SequenceType.Secondary)
                )}
                infoText={sequenceInfoText}
                setLastCursorPosition={setLastCursorPosition}
                isAntiSense
                additionalLabel={
                  showAdditionalLabel
                    ? intel.formatMessage({
                        id: 'THREE_MOD_PHOSPHATE',
                        defaultMessage: "3' Modification: Phosphate",
                      })
                    : undefined
                }
                convertTBasesToUBases={convertTBasesToUBases}
                disabled={
                  !!formik.selectedOligoValues.autoGenerateOligo || !validSense
                }
              />
            )}
          </Grid>
          {isMirna && (
            <Grid container item xs={12} spacing={2}>
              <Grid item xs={12}>
                <Typography style={{ fontWeight: 'bold' }}>
                  <FormattedMessage
                    id="CONF_DUPLEX_SENSE_OLIGO_SEQUENCE_UPDATED"
                    defaultMessage="Sense Sequence (5’-3’)"
                  />
                </Typography>
                <div>{formik.selectedOligoValues.sequence || '--'}</div>
              </Grid>
              <Grid item xs={12}>
                <Typography style={{ fontWeight: 'bold' }}>
                  <FormattedMessage id="OVERHANG" defaultMessage="Overhang" />
                </Typography>
                <div>[dT][dT]</div>
              </Grid>
            </Grid>
          )}
          {showOverhangDropdown && (
            <Grid item xs={3}>
              <OverHangModifications
                name="customerAntisenseOverHang"
                disabled={!validSense}
                label={
                  <FormattedMessage
                    id="CONF_OVERHANG_LABEL"
                    defaultMessage="Overhang"
                  />
                }
              />
            </Grid>
          )}
          {(enableAddModification || enableInternalModification) && (
            <Grid
              item
              xs={12}
              style={{
                marginTop:
                  product === BiTubeProduct.LightCycler
                    ? '16px'
                    : product === BiTubeProduct.mirna
                      ? '18px'
                      : '0',
              }}
            >
              <Modifications
                insertModificationIntoSequence={insertModificationIntoSequence}
                showCommonAccordion={enableAddModification ? true : false}
                sequenceType={
                  isMirna ? SequenceType.Primary : SequenceType.Secondary
                }
              />
            </Grid>
          )}
        </Grid>
        {enableAddBase && (
          <Grid item container xs={12} className={classes.baseContainer}>
            <Link
              onClick={toggleShowBasesForAcceptor}
              className={classes.showBasesLink}
            >
              <span className={classes.displayInlineFlex}>
                {showBasesForAcceptor ? (
                  <>
                    <ExpandMoreIcon />
                  </>
                ) : (
                  <ExpandLessIcon />
                )}
                <FormattedMessage
                  id="ADD_BASES_OPTIONAL"
                  defaultMessage="Add Bases (optional)"
                />
              </span>
            </Link>
            {showBasesForAcceptor && (
              <Grid item xs={12}>
                <Modifications
                  insertModificationIntoSequence={
                    insertModificationIntoSequence
                  }
                  showCommonAccordion={false}
                  sequenceType={SequenceType.Secondary}
                  showBasesForAcceptor={showBasesForAcceptor}
                />
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
      <Grid item xs={12}>
        <Grid item xs={12} className={classes.sequencePreviewContainer}>
          <Typography className={classes.titleStyle}>
            <FormattedMessage
              id="SEQUENCE_PREVIEW_AND_PROPERTIES"
              defaultMessage="Sequence preview & Properties"
            />
          </Typography>
          {!isBitubeProduct && (
            <HoverToolTip
              fontSize="19.25px"
              toolTipText={
                <FormattedMessage
                  id="SEQUENCE_PREVIEW_AND_PROPERTIES_HELP_MESSAGE"
                  defaultMessage="Data download available on Review & Confirm step"
                />
              }
            />
          )}
        </Grid>
        <Grid item xs={12}>
          <BiTubeSequencePreview />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <OligoProperties />
      </Grid>
      <Grid item xs={12}>
        <CsrOverride />
      </Grid>
      <Grid item xs={12}>
        <SpecificationsHome />
      </Grid>
      {formik?.allValues?.loading && <CircleLoader />}
    </Grid>
  )
}

export default BiTubes
